import { Check as CheckIcon, Close as CloseIcon, Edit as EditIcon } from '@mui/icons-material';
import { Box, IconButton, TextField } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { useState } from 'react';

const useStyles = makeStyles((_theme) => ({
  button: {
    height: 'fit-content',
  },
  editIcon: {
    opacity: 0.3,
    '&:hover': {
      opacity: 1,
    },
  },
  inputRoot: {
    fontSize: '1rem',
    fontWeight: 400,
    letterSpacing: '0em',
    lineHeight: '1.167',
  },
}));

interface NameProps {
  value: string;
  onUpdate: (value: string) => void;
  maxLength?: number;
  /** When true, blank/whitespace-only saves are blocked client-side. */
  required?: boolean;
  /** Override for the helper text shown when `required` is set and the input is blank. */
  requiredErrorText?: string;
}

export const EditableText = ({
  value,
  onUpdate,
  maxLength,
  required = false,
  requiredErrorText = 'Required',
}: NameProps): JSX.Element => {
  const classes = useStyles();
  const [textValue, setTextValue] = useState(value);
  const [isEditing, setIsEditing] = useState(false);
  const isBlank = textValue.trim() === '';

  return (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      {!isEditing ? (
        <>
          <Box sx={{ fontSize: '24px' }}>{textValue}</Box>
          <IconButton onClick={() => setIsEditing(true)} className={classes.editIcon} size="large">
            <EditIcon />
          </IconButton>
        </>
      ) : (
        <>
          <TextField
            fullWidth
            value={textValue}
            onChange={(evt) => {
              setTextValue(evt.target.value);
            }}
            margin="dense"
            inputProps={{
              className: classes.inputRoot,
              maxLength,
            }}
            // TODO: backend OpenAPI does not enforce non-empty. Add `minLength: 1` to
            // the relevant string schemas in customer-lambdas/doc/webapp.yaml — DB
            // columns are NOT NULL but accept empty strings, so without this
            // client-side guard callers can save blank values (silently breaking
            // labels). Drop the `required` branch here once the backend catches up.
            error={required && isBlank}
            helperText={
              required && isBlank
                ? requiredErrorText
                : maxLength !== undefined
                ? `${textValue.length}/${maxLength}`
                : undefined
            }
            FormHelperTextProps={{ sx: { textAlign: 'right', m: 0 } }}
          />
          <IconButton
            disabled={required && isBlank}
            onClick={() => {
              const next = required ? textValue.trim() : textValue;
              setTextValue(next);
              setIsEditing(false);
              onUpdate(next);
            }}
            className={classes.button}
            size="large"
          >
            <CheckIcon />
          </IconButton>
          <IconButton
            onClick={() => {
              setTextValue(value);
              setIsEditing(false);
            }}
            className={classes.button}
            size="large"
          >
            <CloseIcon />
          </IconButton>
        </>
      )}
    </div>
  );
};
